import React, { useState } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Select, message,
  Space, Row, Col, Tag, Drawer, Switch, Tooltip, List, Typography,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  TeamOutlined, MailOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { customersAPI } from '../api/endpoints';
import { getCustomerStatusColor, getCustomerStatusLabel } from '../utils/helpers';

const { Text } = Typography;

// ─── Modal para agregar contacto ──────────────────────────────────────────────
const AddContactModal = ({ open, onClose, onSave, loading }) => {
  const [form] = Form.useForm();

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSave(values);
      form.resetFields();
    } catch (_) {}
  };

  return (
    <Modal
      title="Agregar Contacto"
      open={open}
      onOk={handleOk}
      onCancel={() => { form.resetFields(); onClose(); }}
      okText="Agregar"
      cancelText="Cancelar"
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="name" label="Nombre" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email', message: 'Email inválido' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="phone" label="Teléfono">
          <Input />
        </Form.Item>
        <Form.Item name="position" label="Cargo">
          <Input />
        </Form.Item>
        <Form.Item
          name="receive_notifications"
          label="Recibir notificaciones de cobranza"
          valuePropName="checked"
          initialValue={false}
        >
          <Switch checkedChildren="Sí" unCheckedChildren="No" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Drawer de gestión de contactos ───────────────────────────────────────────
const ContactsDrawer = ({ open, customer, onClose, onUpdated }) => {
  const [addModalOpen, setAddModalOpen] = useState(false);

  const addMutation = useMutation(
    (data) => customersAPI.addContact(customer?.id, data),
    {
      onSuccess: () => {
        message.success('Contacto agregado');
        setAddModalOpen(false);
        onUpdated();
      },
      onError: () => message.error('Error al agregar contacto'),
    }
  );

  const updateMutation = useMutation(
    ({ contactId, data }) => customersAPI.updateContact(customer?.id, contactId, data),
    {
      onSuccess: () => { message.success('Contacto actualizado'); onUpdated(); },
      onError: () => message.error('Error al actualizar contacto'),
    }
  );

  const deleteMutation = useMutation(
    (contactId) => customersAPI.deleteContact(customer?.id, contactId),
    {
      onSuccess: () => { message.success('Contacto eliminado'); onUpdated(); },
      onError: () => message.error('Error al eliminar contacto'),
    }
  );

  const handleToggleNotifications = (contact) => {
    updateMutation.mutate({
      contactId: contact.id,
      data: { receive_notifications: !contact.receive_notifications },
    });
  };

  const handleDeleteContact = (contactId) => {
    Modal.confirm({
      title: 'Eliminar contacto',
      content: '¿Está seguro que desea eliminar este contacto?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: () => deleteMutation.mutate(contactId),
    });
  };

  const contacts = customer?.contacts || [];

  return (
    <>
      <Drawer
        title={`Contactos — ${customer?.business_name || ''}`}
        open={open}
        onClose={onClose}
        width={520}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            Agregar Contacto
          </Button>
        }
      >
        {contacts.length === 0 ? (
          <Text type="secondary">Sin contactos registrados.</Text>
        ) : (
          <List
            dataSource={contacts}
            renderItem={(contact) => (
              <List.Item
                key={contact.id}
                actions={[
                  <Tooltip title="Recibir notificaciones de cobranza" key="notif">
                    <Switch
                      size="small"
                      checked={contact.receive_notifications}
                      onChange={() => handleToggleNotifications(contact)}
                      loading={updateMutation.isLoading}
                    />
                  </Tooltip>,
                  <Button
                    key="del"
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteContact(contact.id)}
                  />,
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      {contact.name}
                      {contact.is_primary && <Tag color="blue">Principal</Tag>}
                      {contact.receive_notifications && (
                        <Tag color="green" icon={<MailOutlined />}>Notificaciones</Tag>
                      )}
                    </Space>
                  }
                  description={
                    <Space direction="vertical" size={0}>
                      <Text>{contact.email}</Text>
                      {contact.phone && <Text type="secondary">{contact.phone}</Text>}
                      {contact.position && <Text type="secondary">{contact.position}</Text>}
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <AddContactModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={(data) => addMutation.mutateAsync(data)}
        loading={addMutation.isLoading}
      />
    </>
  );
};

// ─── Página principal ─────────────────────────────────────────────────────────
const CustomersPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [contactsCustomer, setContactsCustomer] = useState(null);
  const [form] = Form.useForm();

  const { data: customersData, refetch } = useQuery('customers', () => customersAPI.list());
  const createMutation = useMutation(customersAPI.create);
  const updateMutation = useMutation(({ id, data }) => customersAPI.update(id, data));
  const deleteMutation = useMutation(customersAPI.delete);

  const customers = customersData?.data?.results || [];

  const columns = [
    {
      title: 'Empresa',
      dataIndex: 'business_name',
      key: 'business_name',
    },
    {
      title: 'Contacto',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'RFC',
      dataIndex: 'nif',
      key: 'nif',
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getCustomerStatusColor(status)}>
          {getCustomerStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: 'Contactos',
      key: 'contacts_count',
      render: (_, record) => {
        const notifCount = (record.contacts || []).filter((c) => c.receive_notifications).length;
        return (
          <Space>
            <Tag icon={<TeamOutlined />}>{(record.contacts || []).length}</Tag>
            {notifCount > 0 && (
              <Tooltip title={`${notifCount} contacto(s) recibirán notificaciones`}>
                <Tag color="green" icon={<MailOutlined />}>{notifCount}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Gestionar contactos">
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => setContactsCustomer(record)}
            />
          </Tooltip>
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="primary"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Eliminar Cliente',
      content: '¿Está seguro que desea eliminar este cliente?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          message.success('Cliente eliminado correctamente');
          refetch();
        } catch (error) {
          message.error('Error al eliminar cliente');
        }
      },
    });
  };

  const onFinish = async (values) => {
    try {
      const data = Object.fromEntries(
        Object.entries(values).map(([k, v]) => [k, v ?? ''])
      );
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data });
        message.success('Cliente actualizado correctamente');
      } else {
        await createMutation.mutateAsync(data);
        message.success('Cliente creado correctamente');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      refetch();
    } catch (error) {
      message.error('Error al guardar cliente');
    }
  };

  const handleContactsUpdated = async () => {
    const result = await refetch();
    const updated = (result?.data?.data?.results || []).find(
      (c) => c.id === contactsCustomer?.id
    );
    if (updated) setContactsCustomer(updated);
  };

  return (
    <div>
      <Card
        title="Gestión de Clientes"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setIsModalOpen(true);
            }}
          >
            Nuevo Cliente
          </Button>
        }
      >
        <Table dataSource={customers} columns={columns} rowKey="id" />
      </Card>

      {/* Modal crear/editar cliente */}
      <Modal
        title={editingId ? 'Editar Cliente' : 'Nuevo Cliente'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="business_name"
            label="Nombre de Empresa"
            rules={[{ required: true, message: 'Por favor ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="Nombre del Contacto"
            rules={[{ required: true, message: 'Por favor ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ required: true, type: 'email', message: 'Por favor ingresa un email válido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nif"
            label="RFC"
            rules={[{ required: true, message: 'Por favor ingresa el RFC' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item name="address" label="Dirección">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="city" label="Ciudad">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="state" label="Departamento">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="postal_code" label="Código Postal">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Estado" initialValue="active">
                <Select>
                  <Select.Option value="active">Activo</Select.Option>
                  <Select.Option value="inactive">Inactivo</Select.Option>
                  <Select.Option value="suspended">Suspendido</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Drawer de contactos */}
      <ContactsDrawer
        open={!!contactsCustomer}
        customer={contactsCustomer}
        onClose={() => setContactsCustomer(null)}
        onUpdated={handleContactsUpdated}
      />
    </div>
  );
};

export default CustomersPage;
